<script setup lang="ts">
import { ui } from '../editor/bridge';
import { pickSvgFiles } from '../editor/clipboard';
import { askText } from '../dialogs';
import { ref } from 'vue';
import type { Id } from '@vp/shared-types';
import { useApp } from '../stores/app';
import { t } from '../i18n';
import { exportCanvasArchive } from '../archive';
import Icon from './Icon.vue';
import ExportDialog from './ExportDialog.vue';
import { moduleFormats, runExport, runImport } from '../modules';

/** Top-left pill: home, open canvases as pills with a save-status dot, canvas menu (§6.5). */
const app = useApp();
const menu = ref<{ id: Id; x: number; y: number } | null>(null);
const exportFor = ref<'png' | 'svg' | 'pdf' | null>(null);

function status(id: Id): { cls: string; text: string } {
  const s = app.sessions.get(id);
  if (!s) return { cls: '', text: '' };
  if (s.loading.value) return { cls: 'busy', text: t('canvas.loading', { n: s.loadedCount.value }) };
  if (s.lockState.value === 'readonly') return { cls: 'warn', text: t('canvas.readOnly') };
  switch (s.saveStatus.value) {
    case 'saving':
      return { cls: 'busy', text: t('canvas.saving') };
    case 'error':
      return { cls: 'err', text: t('canvas.saveError') };
    default:
      return { cls: 'ok', text: t('canvas.saved') };
  }
}

function openMenu(e: MouseEvent, id: Id): void {
  const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
  menu.value = { id, x: r.left, y: r.bottom + 6 };
}

function onTab(e: MouseEvent, id: Id): void {
  if (id === app.active) openMenu(e, id);
  else void app.openCanvas(id);
}

async function rename(id: Id): Promise<void> {
  menu.value = null;
  const name = await askText(t('canvas.namePrompt'), app.sessions.get(id)?.meta.value.name ?? '');
  if (name && name.trim()) await app.renameCanvas(id, name.trim());
}

async function duplicate(id: Id): Promise<void> {
  menu.value = null;
  await app.openCanvas(await app.duplicateCanvas(id));
}

async function exportArchive(id: Id): Promise<void> {
  menu.value = null;
  await exportCanvasArchive(id);
}
</script>

<template>
  <div class="float pillbar top-left" role="tablist">
    <button class="icon-btn" :aria-label="t('app.myCanvases')" :title="t('app.myCanvases')" @click="app.go('home')"><Icon name="home" /></button>
    <span class="sep"></span>
    <div v-for="id in app.tabs" :key="id" class="tab" :class="{ active: id === app.active }" role="tab" :aria-selected="id === app.active">
      <button class="tab-name" :title="status(id).text" @click="onTab($event, id)">
        <span class="dot" :class="status(id).cls" :aria-label="status(id).text"></span>
        <span class="name">{{ app.sessions.get(id)?.meta.value.name ?? '…' }}</span>
        <Icon v-if="id === app.active" name="chevronDown" :size="14" />
      </button>
      <button v-if="id !== app.active || app.tabs.length > 1" class="x" :aria-label="t('canvas.close')" :title="t('canvas.close')" @click="app.closeTab(id)"><Icon name="close" :size="12" /></button>
    </div>
    <button class="icon-btn" :aria-label="t('home.newCanvas')" :title="t('home.newCanvas')" @click="app.createCanvas()"><Icon name="plus" :size="16" /></button>
  </div>
  <div v-if="menu" class="menu-backdrop" @click="menu = null" @contextmenu.prevent="menu = null"></div>
  <div v-if="menu" class="menu glass" :style="{ left: menu.x + 'px', top: menu.y + 'px' }" role="menu">
    <p class="menu-status">{{ status(menu.id).text }}</p>
    <button role="menuitem" @click="rename(menu.id)">{{ t('canvas.rename') }}</button>
    <button role="menuitem" @click="duplicate(menu.id)">{{ t('canvas.duplicate') }}</button>
    <hr />
    <button role="menuitem" @click="exportArchive(menu.id)">{{ t('canvas.export') }}</button>
    <button role="menuitem" @click="(exportFor = 'png'), (menu = null)">{{ t('canvas.exportPng') }}</button>
    <button role="menuitem" @click="(exportFor = 'svg'), (menu = null)">{{ t('canvas.exportSvg') }}</button>
    <button v-if="menu.id === app.active && !ui.readOnly" role="menuitem" data-act="import-svg" @click="(menu = null), pickSvgFiles()">{{ t('canvas.importSvg') }}</button>
    <button role="menuitem" data-act="export-pdf" @click="(exportFor = 'pdf'), (menu = null)">{{ t('canvas.exportPdf') }}</button>
    <!-- API 2.2: formats of modules -->
    <template v-if="menu.id === app.active && (moduleFormats('import').length || moduleFormats('export').length)">
      <hr />
      <button v-for="f in moduleFormats('import')" :key="'i' + f.moduleId + f.id" role="menuitem" :data-format="f.moduleId + ':' + f.id" @click="(menu = null), runImport(f.moduleId, f.id, f.accept)">{{ t('canvas.importAs', { name: f.label }) }}</button>
      <button v-for="f in moduleFormats('export')" :key="'e' + f.moduleId + f.id" role="menuitem" :data-format="f.moduleId + ':' + f.id" @click="(menu = null), runExport(f.moduleId, f.id)">{{ t('canvas.exportAs', { name: f.label }) }}</button>
    </template>
    <hr />
    <button role="menuitem" @click="(app.closeTab(menu.id), (menu = null))">{{ t('canvas.close') }}</button>
  </div>
  <ExportDialog v-if="exportFor" :format="exportFor" @close="exportFor = null" />
</template>

<style scoped>
.top-left {
  top: 12px;
  left: 12px;
  max-width: calc(100% - 380px);
  overflow-x: auto;
  scrollbar-width: none;
}
.tab {
  display: flex;
  align-items: center;
  border-radius: var(--radius-control);
  flex: none;
}
.tab.active {
  background: var(--hover);
}
.tab-name {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: none;
  background: none;
  border-radius: var(--radius-control);
  cursor: pointer;
  color: var(--text-2);
  max-width: 220px;
}
.tab.active .tab-name {
  color: var(--text);
  font-weight: 500;
}
.name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--border-strong);
  flex: none;
}
.dot.ok {
  background: #22c55e;
}
.dot.busy {
  background: #f59e0b;
}
.dot.err {
  background: var(--danger);
}
.dot.warn {
  background: #f97316;
}
.x {
  width: 22px;
  height: 22px;
  margin-right: 4px;
  border: none;
  background: none;
  border-radius: var(--radius-control);
  color: var(--text-3);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.x:hover {
  background: var(--border);
  color: var(--text);
}
.menu-backdrop {
  position: fixed;
  inset: 0;
  z-index: 999;
}
.menu {
  border-radius: var(--radius-panel);
}
.menu-status {
  margin: 4px 10px 6px;
  font-size: 11px;
  color: var(--text-3);
}
</style>
